import React, { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    getNotifications, markNotificationRead,
    markAllNotificationsRead, deleteNotification,
} from 'services/timeOff';

const TYPE_ICONS = {
    time_off_submitted:   { icon: '📋', label: 'Time Off Submitted',   color: '#1565C0' },
    time_off_approved:    { icon: '✅', label: 'Time Off Approved',    color: '#2e7d32' },
    time_off_denied:      { icon: '❌', label: 'Time Off Denied',      color: '#c62828' },
    payroll_approved:     { icon: '💰', label: 'Payroll Approved',     color: '#E7730A' },
    paycheck_ready:       { icon: '💵', label: 'Paycheck Ready',       color: '#2e7d32' },
    document_uploaded:    { icon: '📄', label: 'Document Uploaded',    color: '#6a1b9a' },
    timecard_approved:    { icon: '⏱', label: 'Timecard Approved',    color: '#00695c' },
    timecard_rejected:    { icon: '⏱', label: 'Timecard Rejected',    color: '#c62828' },
    onboarding_completed: { icon: '🎉', label: 'Onboarding Completed', color: '#2e7d32' },
    general:              { icon: '🔔', label: 'General',              color: '#555'    },
};

const fmtDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' at '
        + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const Notifications = () => {
    const navigate    = useNavigate();
    const qc          = useQueryClient();
    const companyId   = localStorage.getItem('companyId') || '';
    const queryKey    = ['notifications', companyId, 'company'];

    const { data = [], isLoading } = useQuery({
        queryKey,
        queryFn: () => getNotifications({ recipientId: companyId, recipientType: 'company' }),
        enabled: !!companyId,
        refetchInterval: 60000,
        staleTime: 30000,
    });

    const notifications = Array.isArray(data) ? data : [];
    const unread = notifications.filter(n => !n.isRead).length;

    const mutRead = useMutation({
        mutationFn: markNotificationRead,
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    const mutReadAll = useMutation({
        mutationFn: () => markAllNotificationsRead({ recipientId: companyId, recipientType: 'company' }),
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    const mutDelete = useMutation({
        mutationFn: deleteNotification,
        onSuccess: () => qc.invalidateQueries({ queryKey }),
    });

    const handleClick = useCallback((n) => {
        if (!n.isRead) mutRead.mutate(n.id);
        if (n.link) navigate(n.link);
    }, [mutRead, navigate]);

    return (
        <div style={{ padding: '24px 32px', maxWidth: 800 }}>
            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>Notifications</h2>
                    {unread > 0 && (
                        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
                            {unread} unread notification{unread !== 1 ? 's' : ''}
                        </p>
                    )}
                </div>
                {unread > 0 && (
                    <button
                        onClick={() => mutReadAll.mutate()}
                        style={{
                            padding: '8px 16px', background: '#E7730A', color: '#fff',
                            border: 'none', borderRadius: 8, cursor: 'pointer',
                            fontSize: 13, fontWeight: 600,
                        }}
                    >
                        Mark all as read
                    </button>
                )}
            </div>

            {/* Loading */}
            {isLoading && (
                <div style={{ textAlign: 'center', padding: 60, color: '#aaa' }}>Loading notifications…</div>
            )}

            {/* Empty state */}
            {!isLoading && notifications.length === 0 && (
                <div style={{
                    textAlign: 'center', padding: '60px 24px',
                    background: '#fff', borderRadius: 12,
                    border: '1px solid #f0f0f0',
                }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🔔</div>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#333', marginBottom: 6 }}>No notifications yet</div>
                    <div style={{ fontSize: 13, color: '#aaa' }}>
                        You'll see notifications here when employees or contractors complete onboarding.
                    </div>
                </div>
            )}

            {/* Notification list */}
            {notifications.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
                    {notifications.map((n, idx) => {
                        const meta = TYPE_ICONS[n.type] || TYPE_ICONS.general;
                        return (
                            <div
                                key={n.id}
                                style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 16,
                                    padding: '16px 20px',
                                    background: n.isRead ? '#fff' : '#fff8f2',
                                    borderBottom: idx < notifications.length - 1 ? '1px solid #f5f5f5' : 'none',
                                    transition: 'background 0.15s',
                                }}
                            >
                                {/* Icon */}
                                <div style={{
                                    width: 44, height: 44, borderRadius: '50%',
                                    background: `${meta.color}18`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 22, flexShrink: 0,
                                }}>
                                    {meta.icon}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                                        <span style={{ fontSize: 14, fontWeight: n.isRead ? 500 : 700, color: '#1a1a1a' }}>
                                            {n.title}
                                        </span>
                                        {!n.isRead && (
                                            <span style={{
                                                width: 8, height: 8, borderRadius: '50%',
                                                background: '#E7730A', display: 'inline-block', flexShrink: 0,
                                            }} />
                                        )}
                                    </div>
                                    {n.message && (
                                        <div style={{ fontSize: 13, color: '#666', marginBottom: 4, lineHeight: 1.4 }}>
                                            {n.message}
                                        </div>
                                    )}
                                    <div style={{ fontSize: 12, color: '#aaa' }}>{fmtDate(n.createdAt)}</div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                    {n.link && (
                                        <button
                                            onClick={() => handleClick(n)}
                                            style={{
                                                padding: '6px 14px',
                                                background: '#E7730A', color: '#fff',
                                                border: 'none', borderRadius: 6,
                                                cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            View Profile
                                        </button>
                                    )}
                                    {!n.isRead && !n.link && (
                                        <button
                                            onClick={() => mutRead.mutate(n.id)}
                                            style={{
                                                padding: '6px 12px', background: 'none',
                                                border: '1px solid #ddd', borderRadius: 6,
                                                cursor: 'pointer', fontSize: 12, color: '#666',
                                            }}
                                        >
                                            Mark read
                                        </button>
                                    )}
                                    <button
                                        onClick={() => mutDelete.mutate(n.id)}
                                        style={{
                                            background: 'none', border: 'none',
                                            cursor: 'pointer', color: '#ccc',
                                            fontSize: 18, lineHeight: 1, padding: '2px 4px',
                                        }}
                                        title="Dismiss"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Notifications;
